import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react'
import { assetPreviewBlob, type NoteAsset } from '../assets/assetTypes'
import type { NoteCover } from '../screenshotCleanup/covers'
import { useAssetImage } from '../screenshotCleanup/useAssetImage'
import { useTrimmedBox, type Box } from '../screenshotCleanup/trim'
import { Icon } from './Icon'
import { enlargedPastSharp } from '../utils/imageSharpness'
import type { ImagePreview } from './ImageLightbox'

interface ImageAttachmentsProps {
  assets: NoteAsset[]
  remoteUrls?: ReadonlyMap<string, string>
  editable?: boolean
  compact?: boolean
  compactGallery?: boolean
  highlightedAssetId?: string | null
  highlightAnimationMs?: number
  onRemove?: (asset: NoteAsset) => void
  onPreviewVisible?: (asset: NoteAsset) => void
  onPreviewOpen?: (preview: ImagePreview) => void
  expectedCount?: number
  /** A cleaned note's own picture (Screenshot Clean-up): shown alone in a compact card, zoomed to its box, instead of the gallery. */
  cover?: NoteCover | null
  /** Fetches an image's full version, shown once it arrives: a cover zoomed to its box, or any picture a card shows larger than its thumbnail. */
  loadFullImage?: (asset: NoteAsset) => Promise<Blob>
}

function AssetImage({
  asset,
  remoteUrl,
  onPreviewVisible,
  onPreviewOpen,
  preferThumbnail,
  viewBox,
  loadFullImage,
}: {
  asset: NoteAsset
  remoteUrl?: string
  onPreviewVisible?: (asset: NoteAsset) => void
  onPreviewOpen?: (preview: ImagePreview) => void
  preferThumbnail: boolean
  /** The part of the image to show, [ymin, xmin, ymax, xmax] from 0 to 1000. */
  viewBox?: Box | null
  loadFullImage?: (asset: NoteAsset) => Promise<Blob>
}) {
  const [localUrl, setLocalUrl] = useState<string | null>(null)
  // A cover zoomed into its box always needs the full image; any other picture only when its thumbnail is shown larger than it is.
  const [enlarged, setEnlarged] = useState(false)
  const full = useAssetImage(loadFullImage && (viewBox || enlarged) ? asset : null, undefined, loadFullImage)
  const imageRef = useRef<HTMLImageElement>(null)
  useEffect(() => {
    const image = imageRef.current
    if (!image || !loadFullImage || viewBox || full.full || typeof ResizeObserver === 'undefined') return
    const check = () => { if (enlargedPastSharp(image)) setEnlarged(true) }
    const observer = new ResizeObserver(check)
    observer.observe(image)
    return () => observer.disconnect()
  }, [loadFullImage, viewBox, full.full])
  const blob = assetPreviewBlob(asset, preferThumbnail)

  useLayoutEffect(() => {
    if (!blob) {
      setLocalUrl(null)
      return
    }
    let url: string
    try {
      url = URL.createObjectURL(blob)
    } catch {
      setLocalUrl(null)
      return
    }
    setLocalUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [blob])

  const source = (full.full ? full.source : null) ?? localUrl ?? remoteUrl
  const box = useTrimmedBox(viewBox ? source ?? null : null, viewBox ?? undefined)
  if (!source) {
    const hasSavedRemotePreview = asset.state === 'synced'
      && Boolean(asset.thumbnailStoragePath ?? asset.masterStoragePath)
    return hasSavedRemotePreview ? (
      <span
        className="image-attachment__placeholder skeleton h-full w-full"
        role="status"
        aria-label="Loading saved image"
      />
    ) : (
      <span className="image-attachment__placeholder">Image not yet synchronized</span>
    )
  }
  const alt = asset.originalFileName ?? 'Note image'
  const image = (
    <img
      ref={imageRef}
      src={source}
      alt={alt}
      loading="lazy"
      onLoad={(event) => {
        onPreviewVisible?.(asset)
        if (loadFullImage && !viewBox && !full.full && enlargedPastSharp(event.currentTarget)) setEnlarged(true)
      }}
      data-view-box={box ? '' : undefined}
      style={box ? { '--image-view-box': `inset(${box[0] / 10}% ${100 - box[3] / 10}% ${100 - box[2] / 10}% ${box[1] / 10}%)` } as CSSProperties : undefined}
    />
  )
  if (!onPreviewOpen) return image
  return (
    <button
      type="button"
      className="image-attachment__preview"
      onClick={() => onPreviewOpen({ assetId: asset.id, source, alt })}
      aria-label={`Maximize ${alt}`}
    >
      {image}
    </button>
  )
}

export function ImageAttachments({
  assets,
  remoteUrls = new Map(),
  editable = false,
  compact = false,
  compactGallery = false,
  highlightedAssetId = null,
  highlightAnimationMs,
  onRemove,
  onPreviewVisible,
  onPreviewOpen,
  expectedCount = assets.length,
  cover = null,
  loadFullImage,
}: ImageAttachmentsProps) {
  const visibleAssets = assets.filter((asset) => asset.state !== 'deleted')
  const coverAsset = compact && cover ? visibleAssets.find((asset) => asset.id === cover.assetId) : undefined
  if (coverAsset) {
    return (
      <div className="image-attachments image-attachments--compact" aria-label="Picture from this note">
        <figure className="image-attachment" data-asset-id={coverAsset.id}>
          <AssetImage asset={coverAsset} remoteUrl={remoteUrls.get(coverAsset.id)} onPreviewVisible={onPreviewVisible} preferThumbnail viewBox={cover?.box} loadFullImage={loadFullImage} />
        </figure>
      </div>
    )
  }
  const totalCount = Math.max(expectedCount, visibleAssets.length)
  const missingCount = Math.max(0, totalCount - visibleAssets.length)
  const showCompactGallery = compact && compactGallery
  const hasCompactOverflow = showCompactGallery && totalCount > 4
  const displayedAssets = compact
    ? visibleAssets.slice(0, showCompactGallery ? (hasCompactOverflow ? 3 : 4) : 1)
    : visibleAssets
  const compactPendingCount = showCompactGallery && !hasCompactOverflow
    ? Math.min(missingCount, Math.max(0, 4 - displayedAssets.length))
    : 0
  const compactGalleryCellCount = showCompactGallery
    ? displayedAssets.length + compactPendingCount + (hasCompactOverflow ? 1 : 0)
    : 0
  const className = [
    'image-attachments',
    compact ? 'image-attachments--compact' : '',
    editable ? 'image-attachments--editable' : '',
    totalCount > 1 ? 'image-attachments--multiple' : '',
    showCompactGallery && totalCount > 2 ? 'image-attachments--grid' : '',
    compactGalleryCellCount > 0 ? `image-attachments--compact-gallery-count-${compactGalleryCellCount}` : '',
  ].filter(Boolean).join(' ')

  if (visibleAssets.length === 0 && missingCount === 0) return null
  return (
    <div className={className} aria-label="Images in this note">
      {displayedAssets.map((asset) => {
        const highlighted = asset.id === highlightedAssetId
        return (
          <figure
            className={highlighted
              ? 'image-attachment image-attachment--paste-revealed'
              : 'image-attachment'}
            data-asset-id={asset.id}
            key={asset.id}
            style={highlighted && highlightAnimationMs !== undefined
              ? { animationDuration: `${highlightAnimationMs}ms` }
              : undefined}
          >
            <AssetImage
              asset={asset}
              remoteUrl={remoteUrls.get(asset.id)}
              onPreviewVisible={onPreviewVisible}
              onPreviewOpen={editable ? onPreviewOpen : undefined}
              preferThumbnail={compact}
              loadFullImage={compact ? loadFullImage : undefined}
            />
            {editable && onRemove ? (
              <button
                type="button"
                className="image-attachment__remove"
                onClick={() => onRemove(asset)}
                aria-label="Remove image"
                title="Remove image"
              >
                <Icon name="trash" />
              </button>
            ) : null}
          </figure>
        )
      })}
      {Array.from({ length: compactPendingCount }, (_, index) => (
        <figure className="image-attachment image-attachment--pending" key={`pending-${index}`}>
          <span className="image-attachment__placeholder">Image syncing…</span>
        </figure>
      ))}
      {hasCompactOverflow ? (
        <figure
          className="image-attachment image-attachment--more"
          aria-label={`${totalCount - displayedAssets.length} more images`}
        >
          <span>+{totalCount - displayedAssets.length}</span>
        </figure>
      ) : null}
      {missingCount > 0 && (!compact || (!compactGallery && visibleAssets.length === 0)) ? (
        <figure className="image-attachment image-attachment--pending">
          <span className="image-attachment__placeholder">Image syncing…</span>
        </figure>
      ) : null}
    </div>
  )
}
